/**
 * Lazy Firebase Auth Loader
 * Delays loading of Firebase Auth and its 90KB iframe until actually needed
 * This improves initial page load performance for anonymous visitors
 */

let firebaseAuthPromise = null;
let firebaseAuthModule = null;

/**
 * Lazy load Firebase Auth only when needed
 * @returns {Promise<Object>} Firebase Auth module with auth, ensureAnonymousAuth, etc.
 */
export async function loadFirebaseAuth() {
  // If already loaded, return cached module
  if (firebaseAuthModule) {
    return firebaseAuthModule;
  }

  // If loading in progress, return existing promise
  if (firebaseAuthPromise) {
    return firebaseAuthPromise;
  }

  // Start loading Firebase Auth
  firebaseAuthPromise = import('./firebaseApp.js').then((module) => {
    firebaseAuthModule = module;
    return module;
  });

  return firebaseAuthPromise;
}

/**
 * Get Firebase Auth instance (loads if not already loaded)
 * @returns {Promise<Object>} Firebase Auth instance
 */
export async function getAuth() {
  const module = await loadFirebaseAuth();
  return module.auth;
}

/**
 * Get Firebase App instance (loads if not already loaded)
 * @returns {Promise<Object>} Firebase App instance
 */
export async function getApp() {
  const module = await loadFirebaseAuth();
  return module.app;
}

/**
 * Ensure anonymous authentication (loads Firebase Auth if needed)
 * @returns {Promise<Object>} User object
 */
export async function ensureAnonymousAuth() {
  const module = await loadFirebaseAuth();
  return module.ensureAnonymousAuth();
}

/**
 * Get Firebase Analytics instance (loads if not already loaded)
 * @returns {Promise<Object|null>} Firebase Analytics instance or null
 */
export async function getAnalytics() {
  const module = await loadFirebaseAuth();
  return module.analytics;
}

/**
 * Get emulator config (loads if not already loaded)
 * @returns {Promise<Object>} Emulator config
 */
export async function getEmulatorConfig() {
  const module = await loadFirebaseAuth();
  return module.emulatorConfig;
}
